import { ConnectProto } from "@racoon/api";
import { getEnumName, getEnumValue } from "@racoon/lib";
import type {
  ScheduleCreateArg,
  SchedulePreviewArg,
  ScheduleUpdateArg,
} from "~/shared/api/container-group-schedule/container-group-schedule.grpc";
import type { ScheduleForm, ScheduleFormError, WindowForm } from "../model/types";

type Rules = NonNullable<ScheduleCreateArg["rules"]>;
type WindowLike = { from?: string; to?: string };

export const DEFAULT_WINDOW: WindowForm = { from: "06:00", to: "12:00" };

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Ids arrive as strings (normalizeBigInt) though typed as bigint. */
const toBigInt = (id: string | bigint) => BigInt(String(id));

const windowsToForm = (windows: WindowLike[] = []): WindowForm[] =>
  windows.map(({ from = "", to = "" }) => ({ from, to }));

export const emptyScheduleForm = (containerGroupId = "", validFrom = ""): ScheduleForm => ({
  containerGroupId,
  jobType: "",
  serviceProviderId: "",
  validFrom,
  validTo: "",
  kind: "weekly",
  months: [],
  weekly: [],
  monthly: [],
  everyNDays: { n: 1, windows: [{ ...DEFAULT_WINDOW }] },
});

/** A pre-table cron row: editing it starts from empty rules and saving makes it native. */
export const isLegacySchedule = (schedule: ConnectProto.ContainerGroupSchedule): boolean =>
  !!schedule.legacyExpr || !schedule.rules?.rule?.case;

export const scheduleToForm = (schedule: ConnectProto.ContainerGroupSchedule): ScheduleForm => {
  const form: ScheduleForm = {
    ...emptyScheduleForm(String(schedule.containerGroupId), schedule.validFrom ?? ""),
    // A job type newer than this build keeps a non-empty name: Radix Select rejects "".
    jobType:
      getEnumName(ConnectProto.JobTypeSchema, schedule.jobType) ?? `JOB_TYPE_${schedule.jobType}`,
    serviceProviderId: schedule.serviceProviderId ? String(schedule.serviceProviderId) : "",
    validTo: schedule.validTo ?? "",
  };
  const rule = schedule.rules?.rule;
  switch (rule?.case) {
    case "weekly":
      return {
        ...form,
        kind: "weekly",
        months: [...rule.value.months],
        weekly: rule.value.days.map((day) => ({
          dow: day.dow,
          weeks: [...day.weeks],
          windows: windowsToForm(day.windows),
        })),
      };
    case "monthly":
      return {
        ...form,
        kind: "monthly",
        months: [...rule.value.months],
        monthly: rule.value.days
          .map((day) => ({ day: day.day, windows: windowsToForm(day.windows) }))
          .sort((a, b) => a.day - b.day),
      };
    case "everyNDays":
      return {
        ...form,
        kind: "everyNDays",
        everyNDays: { n: rule.value.n, windows: windowsToForm(rule.value.windows) },
      };
    default:
      return form;
  }
};

export const formToRules = (form: ScheduleForm): Rules => {
  const months = [...form.months].sort((a, b) => a - b);
  switch (form.kind) {
    case "weekly":
      return {
        rule: {
          case: "weekly",
          value: {
            months,
            days: form.weekly.map((day) => ({
              dow: day.dow,
              weeks: [...day.weeks].sort((a, b) => a - b),
              windows: day.windows,
            })),
          },
        },
      };
    case "monthly":
      return {
        rule: {
          case: "monthly",
          value: {
            months,
            days: form.monthly.map((day) => ({
              day: day.day,
              windows: day.windows,
            })),
          },
        },
      };
    case "everyNDays":
      return {
        rule: {
          case: "everyNDays",
          value: { n: form.everyNDays.n, windows: form.everyNDays.windows },
        },
      };
  }
};

const jobTypeValue = (name: string) =>
  getEnumValue(ConnectProto.JobTypeSchema, name) as ConnectProto.JobType | undefined;

/** The form as saved: a hidden depot select sends no depot, even one picked for another group before a switch. */
export const formToSave = (form: ScheduleForm, providerShown: boolean): ScheduleForm =>
  providerShown ? form : { ...form, serviceProviderId: "" };

export const formToCreate = (form: ScheduleForm): ScheduleCreateArg => ({
  containerGroupId: toBigInt(form.containerGroupId),
  jobType: jobTypeValue(form.jobType),
  serviceProviderId: form.serviceProviderId ? toBigInt(form.serviceProviderId) : undefined,
  validFrom: form.validFrom,
  validTo: form.validTo || undefined,
  rules: formToRules(form),
});

/**
 * The container group and job type of a schedule are fixed; an emptied field is cleared explicitly.
 * The provider goes only when changed: a sent one is checked again, and a stale stored one must not block the edit.
 */
export const formToUpdate = (
  schedule: ConnectProto.ContainerGroupSchedule,
  form: ScheduleForm,
): ScheduleUpdateArg => {
  const stored = schedule.serviceProviderId ? String(schedule.serviceProviderId) : "";
  const providerChanged = form.serviceProviderId !== stored;
  return {
    id: toBigInt(schedule.id),
    serviceProviderId:
      providerChanged && form.serviceProviderId ? toBigInt(form.serviceProviderId) : undefined,
    clearServiceProviderId: providerChanged && !form.serviceProviderId,
    validFrom: form.validFrom,
    validTo: form.validTo || undefined,
    clearValidTo: !form.validTo,
    rules: formToRules(form),
  };
};

/** The draft PreviewSchedule evaluates; the edited schedule is left out of the conflicts. */
export const formToPreview = (
  form: ScheduleForm,
  editingId?: string | bigint,
): SchedulePreviewArg => ({
  source: {
    case: "draft",
    value: {
      containerGroupId: form.containerGroupId ? toBigInt(form.containerGroupId) : undefined,
      jobType: form.jobType ? jobTypeValue(form.jobType) : undefined,
      serviceProviderId: form.serviceProviderId ? toBigInt(form.serviceProviderId) : undefined,
      validFrom: form.validFrom || undefined,
      validTo: form.validTo || undefined,
      rules: formToRules(form),
    },
  },
  excludeScheduleId: editingId ? toBigInt(editingId) : undefined,
});

const minutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

/** Strict HH:MM, end after start, no overlaps — as the backend compiles them. */
const windowsValid = (windows: WindowForm[]): boolean => {
  if (windows.length === 0) return false;
  if (windows.some(({ from, to }) => !HHMM.test(from) || !HHMM.test(to))) return false;
  const sorted = [...windows].sort((a, b) => minutes(a.from) - minutes(b.from));
  return sorted.every(
    (w, i) =>
      minutes(w.to) > minutes(w.from) && (i === 0 || minutes(w.from) >= minutes(sorted[i - 1].to)),
  );
};

/** The same weekday twice for one week of the month (no weeks = every week). */
const weeksOverlap = (days: ScheduleForm["weekly"]): boolean =>
  days.some((a, i) =>
    days
      .slice(i + 1)
      .some(
        (b) =>
          a.dow === b.dow &&
          (a.weeks.length === 0 ||
            b.weeks.length === 0 ||
            a.weeks.some((w) => b.weeks.includes(w))),
      ),
  );

/** The first problem that keeps the form from saving, or null. */
export const validateScheduleForm = (form: ScheduleForm): ScheduleFormError | null => {
  if (!form.containerGroupId) return "containerGroupRequired";
  if (!form.jobType) return "jobTypeRequired";
  if (!form.validFrom) return "validFromRequired";
  if (form.validTo && form.validTo < form.validFrom) return "periodInvalid";
  switch (form.kind) {
    case "weekly":
      if (form.weekly.length === 0) return "noDays";
      if (weeksOverlap(form.weekly)) return "weeksOverlap";
      return form.weekly.every((day) => windowsValid(day.windows)) ? null : "windowInvalid";
    case "monthly":
      if (form.monthly.length === 0) return "noDays";
      return form.monthly.every((day) => windowsValid(day.windows)) ? null : "windowInvalid";
    case "everyNDays":
      if (!Number.isInteger(form.everyNDays.n) || form.everyNDays.n < 1) return "nInvalid";
      return windowsValid(form.everyNDays.windows) ? null : "windowInvalid";
  }
};

/** A weekday's pick in the editor: off, every week, or some weeks of the month (1-5). */
export type WeekMode = "none" | "every" | "weeks";

export const weekMode = (form: ScheduleForm, dow: number): WeekMode => {
  const day = form.weekly.find((d) => d.dow === dow);
  if (!day) return "none";
  return day.weeks.length > 0 ? "weeks" : "every";
};

/** Sets a weekday's weeks ([] for every week, undefined to switch it off); a picked day keeps its window. */
export const setWeekdayWeeks = (form: ScheduleForm, dow: number, weeks?: number[]) => {
  const day = form.weekly.find((d) => d.dow === dow);
  if (!weeks) return form.weekly.filter((d) => d.dow !== dow);
  const next = {
    dow,
    weeks: [...weeks].sort((a, b) => a - b),
    windows: day?.windows ?? [{ ...DEFAULT_WINDOW }],
  };
  if (!day) return [...form.weekly, next];
  // In place, so the day keeps its position; its other entries go
  return form.weekly.filter((d) => d.dow !== dow || d === day).map((d) => (d === day ? next : d));
};

/** Toggles one week of a weekday; its last week stays (switch to every week or off with the select). */
export const toggleWeek = (form: ScheduleForm, dow: number, week: number) => {
  const weeks = form.weekly.find((d) => d.dow === dow)?.weeks ?? [];
  if (weeks.length === 1 && weeks[0] === week) return form.weekly;
  return setWeekdayWeeks(
    form,
    dow,
    weeks.includes(week) ? weeks.filter((w) => w !== week) : [...weeks, week],
  );
};

/** All weeks a weekday's entries name, sorted; [] when one of them is every week. */
export const weekdayWeeks = (form: ScheduleForm, dow: number): number[] => {
  const days = form.weekly.filter((d) => d.dow === dow);
  return days.some((d) => d.weeks.length === 0)
    ? []
    : [...new Set(days.flatMap((d) => d.weeks))].sort((a, b) => a - b);
};

const firstWindow = (windows: WindowForm[]) => [windows[0] ?? { ...DEFAULT_WINDOW }];

/**
 * The form as the editor shows it: one window a day, one entry per weekday, every month.
 * A stored schedule richer than that is saved in this shape.
 */
export const simplifyForm = (form: ScheduleForm): ScheduleForm => ({
  ...form,
  months: [],
  weekly: form.weekly
    .filter((day, i) => form.weekly.findIndex((d) => d.dow === day.dow) === i)
    // A weekday stored in several entries keeps all their weeks, with the first one's window.
    .map((day) => ({
      ...day,
      weeks: weekdayWeeks(form, day.dow),
      windows: firstWindow(day.windows),
    })),
  monthly: form.monthly.map((day) => ({ ...day, windows: firstWindow(day.windows) })),
  everyNDays: { ...form.everyNDays, windows: firstWindow(form.everyNDays.windows) },
});

// Order-free: a weekday changed and changed back reads as the rule it was.
const rulesKey = ({ kind, months, weekly, monthly, everyNDays }: ScheduleForm) =>
  JSON.stringify([
    kind,
    [...months].sort((a, b) => a - b),
    [...weekly]
      .sort((a, b) => a.dow - b.dow)
      .map((d) => ({ ...d, weeks: [...d.weeks].sort((a, b) => a - b) })),
    [...monthly].sort((a, b) => a.day - b.day),
    everyNDays,
  ]);

/** Whether the editor shows a simpler rule than the one stored (and saving it would drop the rest). */
export const isSimplified = (form: ScheduleForm) => rulesKey(simplifyForm(form)) !== rulesKey(form);

/** The form to save: rules left as the editor simplified them keep the stored richer ones. */
export const withStoredRules = (form: ScheduleForm, stored?: ScheduleForm): ScheduleForm =>
  stored && rulesKey(form) === rulesKey(simplifyForm(stored))
    ? {
        ...form,
        kind: stored.kind,
        months: stored.months,
        weekly: stored.weekly,
        monthly: stored.monthly,
        everyNDays: stored.everyNDays,
      }
    : form;
